import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Monitor, Bell, ChevronDown, Loader2, LogOut, Search } from "lucide-react";
import GlobalSearch, { MobileSearch } from "../GlobalSearch/GlobalSearch";
import NotificationDrawer from "../Notification/NotificationDrawer";
import LogoutModal from "../SideNav/modal/logoutModal";
import { toast } from "sonner";
import { FLEETRIFYROUTEPATH } from "../../routes/routesPath";
import { useSingleUserController } from "../../controllers/useSingleUserController";
import { usePermission } from "../../hooks/usePermission";
import { useSelector } from "react-redux";
import { selectAuth } from "../../redux/slices/authSlice";
import { STAFF_MANAGEMENT_PERMISSIONS } from "../../constants/permissions";
import { getInitials, getAvatarColor } from "../../utils/avatar";
import {
  useGetCompanyInfoQuery,
  useGetCompanyStaffQuery,
  useGetStaffNotificationsQuery,
  useDeleteStaffNotificationMutation,
  useMarkStaffNotificationReadMutation,
} from "../../redux/services/settingsApi";

const TopNav = ({ onMenuClick, isSideRes }: { onMenuClick?: () => void; isSideRes?: boolean }) => {
  const navigate = useNavigate();
  const { userDetails } = useSingleUserController();
  const name = userDetails?.name || "Username";
  const email = userDetails?.email || "Email";
  const getRoleName = (roleData: any): string => {
    if (typeof roleData === "object" && roleData?.name) {
      return roleData.name;
    }
    return typeof roleData === "string" ? roleData : "Role";
  };
  const role = getRoleName(userDetails?.role);
  const profilePic = userDetails?.profile_image || null;

  const auth = useSelector(selectAuth);
  const { permissionNames } = usePermission();
  const isSuperAdmin =
    !!auth?.user?.is_head_of_org ||
    auth?.isAdmin ||
    auth?.user?.role?.name === "Super Admin";
  const canAccessUserManagement =
    isSuperAdmin || STAFF_MANAGEMENT_PERMISSIONS.some((p) => permissionNames.includes(p));

  const handleAvatarClick = () => {
    if (!canAccessUserManagement) {
      toast.error("You don't have permission to access User Management");
      return;
    }
    navigate(FLEETRIFYROUTEPATH.USER);
  };

  const [showMenu, setShowMenu] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [leftPosition, setLeftPosition] = useState(0);
  const [showNotifyDrawer, setShowNotifyDrawer] = useState(false);

  const { data: companyInfoData } = useGetCompanyInfoQuery();
  const companyLogo = companyInfoData?.data?.companyInfo?.company_logo;
  const companyName = companyInfoData?.data?.companyInfo?.company_name;
  const companyId = companyInfoData?.data?.companyInfo?.id;

  const { data: staffData } = useGetCompanyStaffQuery(
    { companyId },
    { skip: !companyId }
  );

  const staffMember = staffData?.data?.staff_members;
  const userId = localStorage.getItem("userId");

  const staffMemberId = staffMember?.find(
    (staff: any) => staff.user_id === userId
  )?.id;

  const { data: rawNotifications = [], isLoading, refetch } = useGetStaffNotificationsQuery(
    { staffMemberId: staffMemberId || "" },
    { skip: !staffMemberId }
  );

  const [deleteNotification] = useDeleteStaffNotificationMutation();
  const [markNotificationRead] = useMarkStaffNotificationReadMutation();

  const handleDismiss = async (notificationId: string) => {
    if (!staffMemberId) return;
    try {
      await deleteNotification({ staffMemberId, notificationId }).unwrap();
      refetch();
      toast.success("Notification deleted successfully");
    } catch (error) {
      console.error("Failed to delete notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    if (!staffMemberId) return;
    try {
      await markNotificationRead({ staffMemberId, notificationId }).unwrap();
      refetch();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      toast.error("Failed to mark notification as read");
    }
  };

  // "negotiation_requested" carries the customer's own (optional) message, so
  // build a readable line around it, and link straight to the order.
  const describeNotification = (data: any): string => {
    if (data?.type === "negotiation_requested") {
      const order = data.order_number ? `order ${data.order_number}` : "an order";
      return data.message
        ? `A customer wants to negotiate ${order}: “${data.message}”`
        : `A customer wants to negotiate ${order}.`;
    }
    // Newer notification types (e.g. cash_payment_confirmed) send
    // title/body instead of a single message field — fall back through both.
    return data?.message || data?.body || data?.title || "No message";
  };

  const notificationLink = (data: any): string | undefined => {
    if (data?.type !== "negotiation_requested" || !data?.order_id) return undefined;
    const id = String(data.order_id);
    return /relocation/.test(data.order_type ?? "")
      ? `${FLEETRIFYROUTEPATH.RELOCATION_QUOTES}?order=${encodeURIComponent(id)}`
      : FLEETRIFYROUTEPATH.VIEW_ORDER.replace(":orderId", id);
  };

  const handleOpenNotification = (notification: { id: string; href?: string; isNew?: boolean }) => {
    if (!notification.href) return;
    if (notification.isNew) handleMarkAsRead(notification.id);
    setShowNotifyDrawer(false);
    navigate(notification.href);
  };

  const notificationsData = rawNotifications.map((n: any) => ({
    id: n.id,
    message: describeNotification(n.data),
    href: notificationLink(n.data),
    timestamp: new Date(n.created_at).toLocaleString(),
    // Kept as the raw type (not the human title) — getSenderMeta matches
    // against this string to pick an icon, and it stays short for the pill.
    sender: n.data?.type || "System",
    avatarText: n.data?.type || "S",
    isNew: n.read_at === null,
  }));

  const handleShowNotifyDrawer = () => {
    if (staffMemberId) refetch();
    setShowNotifyDrawer((prev) => !prev);
  };

  const notifyCount = notificationsData.filter((n) => n.isNew).length;

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setLeftPosition(isSideRes ? 90 : 240);
      else setLeftPosition(0);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSideRes]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  return (
    <>
      <header
        className="fixed top-0 z-50 bg-white border-b border-gray-100 shadow-sm transition-all duration-300"
        style={{
          height: 72,
          left: leftPosition,
          right: leftPosition === 0 ? 0 : "auto",
          width: leftPosition === 0 ? "100%" : `calc(100% - ${leftPosition}px)`,
        }}
      >
        <div className="flex items-center justify-between px-3 h-full">

          {/* left section */}
          <div className="flex items-center">
            {onMenuClick && (
              <button
                onClick={onMenuClick}
                className="p-2 rounded hover:bg-gray-50 block md:hidden"
              >
                <span className="block w-6 h-0.5 bg-gray-800 mb-1" />
                <span className="block w-6 h-0.5 bg-gray-800 mb-1" />
                <span className="block w-6 h-0.5 bg-gray-800" />
              </button>
            )}
            <div className="hidden md:flex items-center md:ml-20 lg:ml-5">
              <div id="topnav-search">
                <GlobalSearch />
              </div>
            </div>
          </div>

          {/* right section */}
          <div className="flex items-center gap-1.5 md:gap-2">
            {/* Mobile search trigger */}
            <button
              type="button"
              aria-label="Open search"
              onClick={() => setMobileSearchOpen(true)}
              className="p-2 rounded hover:bg-gray-50 md:hidden"
            >
              <Search size={18} />
            </button>

            <button
              id="topnav-video-guide"
              type="button"
              aria-label="Dashboard view"
              className="p-2 rounded hover:bg-gray-50"
              onClick={() => navigate(FLEETRIFYROUTEPATH.VIDEO_GUIDE)}
            >
              <motion.span
                className="inline-flex"
                animate={{ scale: [1, 1.15, 1], opacity: [1, 0.75, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1.5, ease: "easeInOut" }}
              >
                <Monitor size={18} />
              </motion.span>
            </button>

            {/* notification bell */}
            <div id="topnav-notifications" className="relative">
              <button
                onClick={handleShowNotifyDrawer}
                className="p-2 rounded hover:bg-gray-50"
                aria-label="Notifications"
                type="button"
              >
                {isLoading ? (
                  <Loader2 className="w-[18px] h-[18px] text-gray-400 animate-spin" />
                ) : (
                  <motion.span
                    className="inline-flex"
                    animate={notifyCount > 0 ? { rotate: [-12, 12] } : { rotate: 0 }}
                    transition={
                      notifyCount > 0
                        ? { duration: 1.4, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }
                        : { duration: 0.3 }
                    }
                    style={{ originX: "50%", originY: "0%" }}
                  >
                    <Bell size={18} className={notifyCount > 0 ? "text-[#143887]" : "text-gray-500"} />
                  </motion.span>
                )}
              </button>
              {notifyCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#FF4D4F] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white px-1">
                  {notifyCount > 99 ? "99+" : notifyCount}
                </span>
              )}
            </div>

            {/* profile / logo */}
            <div id="topnav-profile" className="relative" ref={dropdownRef}>
              <div className="flex items-center gap-1.5 md:gap-2">
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  className="h-9 w-9 rounded-full flex items-center justify-center text-white overflow-hidden"
                  style={
                    !profilePic && !companyLogo
                      ? { backgroundColor: getAvatarColor(name !== "Username" ? name : companyName) }
                      : { backgroundColor: "#143887" }
                  }
                >
                  {profilePic ? (
                    <img src={profilePic} alt="Profile" className="h-full w-full object-cover" />
                  ) : companyLogo ? (
                    <img src={companyLogo} alt="Company Logo" className="h-full w-full object-cover" />
                  ) : (
                    <span className="text-xs font-semibold">
                      {name !== "Username" ? getInitials(name) : getInitials(companyName)}
                    </span>
                  )}
                </button>

                <div className="hidden sm:flex flex-col items-start leading-tight">
                  <span className="text-sm font-medium capitalize">{name}</span>
                  <p className="text-xs text-gray-500">{role}</p>
                </div>

                <button
                  onClick={() => setShowMenu((prev) => !prev)}
                  type="button"
                  className="p-1 rounded hover:bg-gray-50"
                >
                  <ChevronDown size={16} />
                </button>
              </div>

              {showMenu && (
                <div className="absolute right-0 mt-5 w-56 min-w-max bg-white border rounded-lg shadow-lg z-50 p-3">
                  <div className="mb-3 border-b pb-2">
                    <p className="text-sm font-medium text-gray-800">{name}</p>
                    <p className="text-xs text-gray-500">{email}</p>
                  </div>
                  <button
                    onClick={() => setShowLogoutModal(true)}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full rounded"
                  >
                    <LogOut size={16} />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile full-screen search overlay */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 top-[72px] bg-white z-40 flex flex-col md:hidden">
          <MobileSearch onClose={() => setMobileSearchOpen(false)} />
        </div>
      )}

      <NotificationDrawer
        isOpen={showNotifyDrawer}
        setIsOpen={setShowNotifyDrawer}
        isLoading={isLoading}
        notificationsData={notificationsData || []}
        handleDismiss={handleDismiss}
        handleMarkAsRead={handleMarkAsRead}
        handleOpen={handleOpenNotification}
      />

      <LogoutModal
        isModalOpen={showLogoutModal}
        setIsModalOpen={setShowLogoutModal}
      />
    </>
  );
};

export default TopNav;
